import React from 'react';
import { Box, Flex, Heading, Text, Card, CardHeader, CardContent, KPICard, StackedBarChart, LineChart, ChartEmptyState, SkeletonChart, SkeletonKPIRow, Select, DateRangePicker } from '@chatsdet/ui';
import {
  FlowArrow,
  Cube,
  ArrowClockwise,
  Browser,
} from '@phosphor-icons/react';
import { CalendarDate } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';
import type { Key } from 'react-aria-components';
import {
  useUsageSummary,
  useUsageTimeseries,
  useUsageQuota,
  formatCount,
} from '../../../hooks/useUsageData';
import {
  computeBillingPeriod,
  buildCumulativeData,
} from '../../../hooks/usagePeriod';

// ── Colors (Wong colorblind-safe palette) ───────────────────────────────────

const COLORS = {
  blue: '#0072B2',
  amber: '#E69F00',
  teal: '#009E73',
  vermillion: '#D55E00',
  purple: '#CC79A7',
};

// ── Date range presets ──────────────────────────────────────────────────────

type RangePreset =
  | 'current-period'
  | 'previous-period'
  | 'last-7'
  | 'last-30'
  | 'last-90'
  | 'custom';

const PRESET_ITEMS: { id: RangePreset; label: string }[] = [
  { id: 'current-period', label: 'Current billing period' },
  { id: 'previous-period', label: 'Previous billing period' },
  { id: 'last-7', label: 'Last 7 days' },
  { id: 'last-30', label: 'Last 30 days' },
  { id: 'last-90', label: 'Last 90 days' },
  { id: 'custom', label: 'Custom range' },
];

const PRESET_DAYS: Partial<Record<RangePreset, number>> = {
  'last-7': 7,
  'last-30': 30,
  'last-90': 90,
};

// UTC day helpers — usage data and billing periods are bucketed in UTC days.
function utcDateToCalendar(d: Date): CalendarDate {
  return new CalendarDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}
function calendarToUtcDate(c: DateValue): Date {
  return new Date(Date.UTC(c.year, c.month - 1, c.day));
}
function addUtcDays(d: Date, days: number): Date {
  return new Date(d.getTime() + days * 86_400_000);
}

// ── Breakdown Bar ───────────────────────────────────────────────────────────

function BreakdownRow({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
  return (
    <Box>
      <Flex align="center" gap="sm">
        <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: color }} />
        <Text size="sm" className="flex-1">{label}</Text>
        <Text size="sm" className="font-mono">{count.toLocaleString()}</Text>
        <Text size="xs" className="text-muted-foreground w-12 text-right">{pct}%</Text>
      </Flex>
      <div className="h-1.5 bg-muted rounded-full mt-1 mb-3">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${total > 0 ? (count / total) * 100 : 0}%`, backgroundColor: color }}
        />
      </div>
    </Box>
  );
}

// ── Y-axis formatter ────────────────────────────────────────────────────────

function formatYAxis(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
}

// ── Main Component ──────────────────────────────────────────────────────────

/** Format a UTC daily bucket as "Jun 3" (matches buildCumulativeData labels). */
function formatBucketDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function UsageSection() {
  // The preset + range picker is the single date control for the whole page —
  // KPIs, charts, and breakdowns all reflect the selected range. Defaults to
  // the current billing period; picking custom dates switches to 'custom'.
  const [preset, setPreset] = React.useState<RangePreset>('current-period');
  const [customRange, setCustomRange] = React.useState<RangeValue<DateValue> | null>(null);

  // Quota data (drives billing-period presets)
  const quotaQuery = useUsageQuota();
  const quotaData = quotaQuery.data;
  const monthStartDay = quotaData?.quota.monthStartDay ?? 1;
  const monthlyRunQuota = quotaData?.quota.monthlyRunQuota ?? null;

  const isBillingPreset = preset === 'current-period' || preset === 'previous-period';

  // Selected range as UTC instants, end-exclusive (matches the usage queries).
  const selectedRange = React.useMemo(() => {
    if (preset === 'current-period') return computeBillingPeriod(monthStartDay, 0);
    if (preset === 'previous-period') return computeBillingPeriod(monthStartDay, -1);
    const presetDays = PRESET_DAYS[preset];
    if (presetDays !== undefined) {
      // Align to UTC day boundaries so daily buckets line up; includes today.
      const end = addUtcDays(calendarToUtcDate(utcDateToCalendar(new Date())), 1);
      return { start: addUtcDays(end, -presetDays), end };
    }
    if (customRange?.start && customRange?.end) {
      return {
        start: calendarToUtcDate(customRange.start),
        // Picker end day is inclusive; queries take an exclusive bound.
        end: addUtcDays(calendarToUtcDate(customRange.end), 1),
      };
    }
    return computeBillingPeriod(monthStartDay, 0);
  }, [preset, customRange, monthStartDay]);

  // Range picker mirrors the active range (inclusive end day for display).
  const pickerValue = React.useMemo<RangeValue<DateValue>>(
    () => ({
      start: utcDateToCalendar(selectedRange.start),
      end: utcDateToCalendar(addUtcDays(selectedRange.end, -1)),
    }),
    [selectedRange],
  );

  // One timeseries fetch (daily buckets within the selected range) powers both
  // the cumulative chart and the Activity Over Time chart.
  const timeseriesQuery = useUsageTimeseries(
    selectedRange.start.toISOString(),
    selectedRange.end.toISOString(),
    1440,
  );
  const timeseries = React.useMemo(
    () => timeseriesQuery.data?.data ?? [],
    [timeseriesQuery.data],
  );
  const cumulativeData = React.useMemo(
    () => buildCumulativeData(timeseries),
    [timeseries],
  );
  // The monthly quota only applies to billing periods — hide it on day/custom ranges.
  const quotaReferenceLines = React.useMemo(
    () =>
      isBillingPreset && monthlyRunQuota
        ? [{ y: monthlyRunQuota, label: `Quota: ${formatCount(monthlyRunQuota)}` }]
        : undefined,
    [isBillingPreset, monthlyRunQuota],
  );

  // Range usage: prefer quota response for the current period, fall back to cumulative total
  const periodUsage = React.useMemo(() => {
    if (preset === 'current-period' && quotaData) return quotaData.usage.agenticRuns;
    if (cumulativeData.length > 0) return cumulativeData[cumulativeData.length - 1].cumulative;
    return 0;
  }, [preset, quotaData, cumulativeData]);

  // Summary (KPIs + breakdowns) for the same selected range
  const summaryQuery = useUsageSummary(
    selectedRange.start.toISOString(),
    selectedRange.end.toISOString(),
  );
  const summary = summaryQuery.data?.data;

  const cacheHitRate = React.useMemo(() => {
    if (!summary || summary.nodesExecuted === 0) return null;
    const cached = (summary.nodesByTier['l1_cache'] ?? 0) + (summary.nodesByTier['l2_recovery'] ?? 0);
    return ((cached / summary.nodesExecuted) * 100).toFixed(1);
  }, [summary]);

  const chartData = React.useMemo(
    () =>
      timeseries.map((b) => ({
        date: formatBucketDay(b.bucket),
        'Workflow Runs': b.workflowRuns,
        'Steps Executed': b.nodesExecuted,
        'Browser Actions': b.browserActions,
      })),
    [timeseries],
  );

  return (
    <Box className="space-y-6">
      {/* Range control + Cumulative Chart */}
      <Card>
        <CardHeader>
          <Flex justify="between" align="start" gap="md" wrap="wrap">
            <Heading level={3}>{isBillingPreset ? 'Billing Period' : 'Usage'}</Heading>
            <Flex align="center" gap="sm" wrap="wrap">
              <Box className="min-w-[200px]">
                <Select
                  aria-label="Date range preset"
                  items={PRESET_ITEMS.map((p) => ({ id: p.id, label: p.label }))}
                  selectedKey={preset}
                  onSelectionChange={(key: Key | null) => {
                    if (key) setPreset(String(key) as RangePreset);
                  }}
                />
              </Box>
              <DateRangePicker
                aria-label="Date range (UTC days)"
                granularity="day"
                value={pickerValue}
                onChange={(v) => {
                  if (v?.start && v?.end) {
                    setCustomRange(v);
                    setPreset('custom');
                  }
                }}
              />
            </Flex>
          </Flex>
          {quotaQuery.isError ? (
            <Text size="sm" className="text-destructive mt-1">
              Failed to load quota configuration.
            </Text>
          ) : (
            <Box className="mt-1">
              <Text size="sm" className="text-muted-foreground">
                {formatCount(periodUsage)}
                {isBillingPreset && monthlyRunQuota !== null
                  ? ` / ${formatCount(monthlyRunQuota)}`
                  : ''}{' '}
                agentic runs
                {preset === 'current-period' ? ' this period' : ''}
              </Text>
              {isBillingPreset && monthlyRunQuota !== null && monthlyRunQuota > 0 && (
                <div className="h-1.5 bg-muted rounded-full mt-1 max-w-[240px]">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, (periodUsage / monthlyRunQuota) * 100)}%`,
                      backgroundColor: COLORS.blue,
                    }}
                  />
                </div>
              )}
            </Box>
          )}
        </CardHeader>
        <CardContent>
          {timeseriesQuery.isLoading ? (
            <SkeletonChart height={240} />
          ) : timeseriesQuery.isError ? (
            <Text className="text-destructive py-4">Failed to load usage data for this period.</Text>
          ) : cumulativeData.length === 0 ? (
            <ChartEmptyState message="No agentic runs in this period" />
          ) : (
            <LineChart
              data={cumulativeData}
              xDataKey="day"
              lines={[
                { dataKey: 'cumulative', name: 'Cumulative Agentic Runs', color: COLORS.blue },
              ]}
              referenceLines={quotaReferenceLines}
              height={240}
              showLegend={false}
              xAxisInterval="preserveStartEnd"
            />
          )}
        </CardContent>
      </Card>

      {/* KPI Cards (selected period) */}
      {summaryQuery.isLoading ? (
        <SkeletonKPIRow count={4} />
      ) : summaryQuery.isError ? (
        <Card>
          <CardContent className="py-4">
            <Text className="text-destructive">Failed to load usage summary.</Text>
          </CardContent>
        </Card>
      ) : (
        <Flex gap="md" wrap="wrap">
          <Box className="flex-1 min-w-[160px]">
            <KPICard
              title="Workflow Runs"
              formattedValue={summary ? formatCount(summary.workflowRuns) : '0'}
              icon={<FlowArrow size={16} />}
            />
          </Box>
          <Box className="flex-1 min-w-[160px]">
            <KPICard
              title="Steps Executed"
              formattedValue={summary ? formatCount(summary.nodesExecuted) : '0'}
              icon={<Cube size={16} />}
            />
          </Box>
          <Box className="flex-1 min-w-[160px]">
            <KPICard
              title="Cache Hit Rate"
              formattedValue={cacheHitRate !== null ? `${cacheHitRate}%` : 'N/A'}
              icon={<ArrowClockwise size={16} />}
            />
          </Box>
          <Box className="flex-1 min-w-[160px]">
            <KPICard
              title="Browser Actions"
              formattedValue={summary ? formatCount(summary.browserActions) : '0'}
              icon={<Browser size={16} />}
            />
          </Box>
        </Flex>
      )}

      {/* Timeseries Chart */}
      <Card>
        <CardHeader>
          <Heading level={3}>Activity Over Time</Heading>
        </CardHeader>
        <CardContent>
          {timeseriesQuery.isLoading ? (
            <SkeletonChart height={240} />
          ) : timeseriesQuery.isError ? (
            <Text className="text-destructive py-4">Failed to load activity data.</Text>
          ) : chartData.length === 0 ? (
            <ChartEmptyState message="No usage data for this period" />
          ) : (
            <StackedBarChart
              data={chartData}
              xDataKey="date"
              grouped
              stacks={[
                { dataKey: 'Workflow Runs', color: COLORS.blue },
                { dataKey: 'Steps Executed', color: COLORS.amber },
                { dataKey: 'Browser Actions', color: COLORS.teal },
              ]}
              height={240}
              showXAxis
              showLegend
              yAxisWidth={50}
              yAxisTickFormatter={formatYAxis}
              allowDecimals={false}
            />
          )}
        </CardContent>
      </Card>

      {/* Breakdown Cards */}
      {summary && (
        <Flex gap="md" wrap="wrap">
          <Box className="flex-1 min-w-[250px]">
            <Card>
              <CardHeader>
                <Heading level={4}>Step Execution Tiers</Heading>
              </CardHeader>
              <CardContent>
                <BreakdownRow
                  label="L1 Cache"
                  count={summary.nodesByTier['l1_cache'] ?? 0}
                  total={summary.nodesExecuted}
                  color={COLORS.blue}
                />
                <BreakdownRow
                  label="L2 Recovery"
                  count={summary.nodesByTier['l2_recovery'] ?? 0}
                  total={summary.nodesExecuted}
                  color={COLORS.amber}
                />
                <BreakdownRow
                  label="L3 Agent"
                  count={summary.nodesByTier['l3_agent'] ?? 0}
                  total={summary.nodesExecuted}
                  color={COLORS.purple}
                />
              </CardContent>
            </Card>
          </Box>

          <Box className="flex-1 min-w-[250px]">
            <Card>
              <CardHeader>
                <Heading level={4}>Browser Action Mode</Heading>
              </CardHeader>
              <CardContent>
                <BreakdownRow
                  label="Cache Replay"
                  count={summary.browserActionsByMode['cache_replay'] ?? 0}
                  total={summary.browserActions}
                  color={COLORS.teal}
                />
                <BreakdownRow
                  label="Agent"
                  count={summary.browserActionsByMode['agent'] ?? 0}
                  total={summary.browserActions}
                  color={COLORS.vermillion}
                />
              </CardContent>
            </Card>
          </Box>

          <Box className="flex-1 min-w-[250px]">
            <Card>
              <CardHeader>
                <Heading level={4}>Browser Action Type</Heading>
              </CardHeader>
              <CardContent>
                <BreakdownRow
                  label="Read"
                  count={summary.browserActionsByCategory.read}
                  total={summary.browserActions}
                  color={COLORS.blue}
                />
                <BreakdownRow
                  label="Act"
                  count={summary.browserActionsByCategory.act}
                  total={summary.browserActions}
                  color={COLORS.amber}
                />
              </CardContent>
            </Card>
          </Box>
        </Flex>
      )}
    </Box>
  );
}
